






































































import Vue from 'vue';
import { timeDay, timeMonth } from 'd3-time';
import { ISurveyResponseCount } from '@/store/types';
import { TopLevelSpec } from 'vega-lite';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import HeatmapLoader from '@/components/UI/Loader/HeatmapLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';
import { mapGetters, mapState } from 'vuex';
import { LOAD_STATE } from '@/store/shared';
import * as actions from '@/store/action-types';
import { getDateEndpoints } from '@/helpers/date-utils';
import { formatNumber, formatPercent } from '@/helpers/jsUtils';
import { genHeatmapSpec } from '../Vis/Vega/Specs/HeatmapSpec';

interface DeltaStats {
  label: string;
  primary: string;
  sub: string;
  subColor: string;
}

export default Vue.extend({
  name: 'CancellationInsights',
  components: {
    VueVegaEmbed,
    DatePickerCondensed,
    SessionFilter,
    FooterLoader,
    HeaderLoader,
    HeatmapLoader,
  },
  async created() {
    await this.$store.dispatch(actions.FETCH_SURVEY_RESPONSES, { filter: {}, breakdown: ['day'] });
    while (this.responseCount < 15 && this.orgCreatedDate && this.startDate > this.orgCreatedDate) {
      this.startDate = timeMonth.offset(this.startDate, -1);
    }
    if (this.startDate < this.orgCreatedDate) {
      this.startDate = this.orgCreatedDate;
    }
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -30);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      customerFilterSelection: '',
      circularHeatmap: false,
      startDate,
      endDate,
      filter: {} as Record<string, string[]>,
      initialLoading: true,
    };
  },
  computed: {
    ...mapGetters([
      'monthsAgoMetric',
      'monthsAgoMetricDelta',
      'sessionSegments',
      'lastMonthLabel',
      'orgCreatedDate',
      'daysSinceOrgCreated',
      'waitForData',
    ]),
    ...mapState(['surveyResponsesLoadState']),
    dateRange(): [Date, Date] {
      return [this.startDate, this.endDate];
    },
    loading(): boolean {
      return this.surveyResponsesLoadState !== LOAD_STATE.LOADED;
    },
    surveyResponses(): ISurveyResponseCount[] {
      return this.$store.state.surveyResponses;
    },
    filteredResponses(): ISurveyResponseCount[] {
      return this.surveyResponses.filter((d) => d.date > this.startDate && d.date < this.endDate);
    },
    responseCount(): number {
      return this.filteredResponses.reduce((sum, d) => d.count + sum, 0);
    },
    cancellationReasonHeatmap(): TopLevelSpec | null {
      if (this.loading) {
        return null;
      }
      if (this.filteredResponses && this.filteredResponses.length) {
        const totalResponses = this.filteredResponses.reduce((sum, d) => d.count + sum, 0);
        const responseFrequencies = this.filteredResponses.reduce(
          (counts, d) => {
            counts[d.response] = counts[d.response] ? counts[d.response] + d.count : d.count;
            return counts;
          },
          {} as { [key: string]: number }
        );
        const responsePct = Object.keys(responseFrequencies).reduce(
          (pcts, response) => {
            pcts[response] = `${((responseFrequencies[response] / totalResponses) * 100).toFixed(0)}%`;
            return pcts;
          },
          {} as { [key: string]: string }
        );
        const heatmapData = this.filteredResponses
          // eslint-disable-next-line arrow-body-style
          .map((d) => {
            return {
              ...d,
              Date: d.date,
              // @ts-ignore
              'Reason for Cancellation': `${d.response} (${responsePct[d.response]})`,
            };
          });
        return genHeatmapSpec({
          data: heatmapData,
          xField: 'Date',
          yField: 'Reason for Cancellation',
          timeUnit: 'yearmonthdate',
          xLabels: true,
          yLabels: true,
          zField: 'count',
          // yAxisTitle: 'Response',
          circular: this.circularHeatmap,
          // height: 200,
        });
      }
      return null;
    },
    comparisons(): DeltaStats[] {
      if (this.loading) {
        return [];
      }
      const lastValue = this.monthsAgoMetric('lostSubs', 1);
      const stats = [
        {
          label: 'Total Cancellations',
          primary: lastValue ? formatNumber(lastValue) : '-',
          sub: this.lastMonthLabel,
          subColor: 'text-gray-500 text-sm',
        },
      ];
      const comparisons = [
        [1, 'One Month Ago'],
        [2, 'Two Months Ago'],
        [12, 'One Year Ago'],
      ] as [number, string][];
      comparisons.forEach(([diff, label]) => {
        const delta = this.monthsAgoMetricDelta('lostSubs', 1, diff + 1) as number;
        const value = this.monthsAgoMetric('lostSubs', diff + 1) as number | null;
        if (value && delta > 0) {
          stats.push({
            label,
            primary: formatNumber(value),
            sub: `+${formatPercent(delta)}`,
            subColor: 'text-brand-red text-xl',
          });
        } else if (value && delta < 0) {
          stats.push({
            label,
            primary: formatNumber(value),
            sub: formatPercent(delta),
            subColor: 'text-green-600 text-xl',
          });
        } else {
          stats.push({
            label,
            primary: '-',
            sub: '',
            subColor: 'text-gray-500 text-sm',
          });
        }
      });
      const show = stats.reduce((test, d) => d.primary !== '-' || test, false);
      if (!show) {
        return [];
      }
      return stats;
    },
  },
  methods: {
    fetchData() {
      const mappedFilter = Object.keys(this.filter).reduce(
        (acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        },
        {} as Record<string, any>
      );
      // mappedFilter.startDate = this.startDate;
      // mappedFilter.endDate = this.endDate;
      this.$store.dispatch(actions.FETCH_SURVEY_RESPONSES, { filter: mappedFilter, breakdown: ['day'] });
    },
    handleDateChange(value: [Date, Date]): void {
      this.startDate = value[0];
      this.endDate = value[1];
      // this.fetchData();
    },
    onFilterUpdate(newFilter: Record<string, string[]>) {
      this.filter = newFilter;
      this.fetchData();
    },
    exportCancellationInsights() {
      this.$store.dispatch(actions.EXPORT_DATA, {
        // @ts-ignore
        data: this.surveyResponses,
        name: 'survey-responses',
        fields: ['response', 'count', 'date'],
        csv: true,
      });
    },
  },
});
